import { ref, type Ref } from "vue";
import { CacheClosedError, CacheError } from "@/services/cache";

/**
 * Why a store stopped initializing: `evicted` when a newer build upgraded the cache and a reload is in flight
 * (quiet, never reported), `failed` when the cache layer itself failed (reported once by the store).
 */
export type InitHalt = 'evicted' | 'failed';

export interface InitLatch {
    readonly halted: Readonly< Ref< InitHalt | null > >;
    isHalted(): boolean;
    /**
     * Latches on a cache-layer error and returns the reason, or `null` for anything else (a server or mapping
     * failure keeps the store's normal retry-on-next-call behavior).
     */
    halt( err: unknown ): InitHalt | null;
    /** Called from the store's reset(): sign-out, sign-in or a sync-driven re-read re-arms the initializer. */
    clear(): void;
}

/**
 * tmi-7be: a store whose cache read failed must not be re-entered until an explicit reset().
 *
 * Every cache-backed initializer clears its loading flag in `finally`. Once the cache rejects immediately (a newer
 * build evicted this tab, or storage is unavailable), a watcher that re-runs initialization when that flag turns
 * false spins forever in microtasks: each lap is an already-rejected promise plus a Vue scheduler flush, the event
 * loop never yields, a pending `location.reload()` never runs, and the tab freezes (SectorBellCurve's
 * `watch( isLoading )` did exactly this). The entry guard checks this latch so no lap can start.
 */
export function createInitLatch(): InitLatch {
    const halted = ref< InitHalt | null >( null );

    return {
        halted,
        isHalted: () => halted.value !== null,
        halt( err: unknown ): InitHalt | null {
            if ( err instanceof CacheClosedError ) {
                halted.value = 'evicted';
            } else if ( err instanceof CacheError ) {
                halted.value = 'failed';
            }
            return err instanceof CacheError ? halted.value : null;
        },
        clear(): void {
            halted.value = null;
        }
    };
}
